import React, { Component } from 'react';
import { View, Dimensions } from 'react-native';
import GradientText from "react-native-gradient-texts";

export default class ResponsiveGradientText extends Component {
  constructor(props) {
    super(props);
    this.state = {
      containerWidth: 0,
      viewportWidth: 0,
    };
  }

  componentDidMount() {
    // Get initial viewport width for web
    if (typeof window !== 'undefined') {
      this.setState({ viewportWidth: window.innerWidth });
    }
  }

  // Calculate if text fits in one line at given font size
  calculateTextWidth(text, fontSize) {
    // Improved character width calculation for web Nunito font
    // Account for uppercase text and varying character widths
    const avgCharWidth = fontSize * 0.60; // More conservative estimation for web
    return text.length * avgCharWidth;
  }

  // Split text into lines that fit within the container
  splitTextIntoLines(text, maxWidth, fontSize) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const lineWidth = this.calculateTextWidth(testLine, fontSize);
      
      if (lineWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, add it anyway
          lines.push(word);
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }

  // Get effective container width considering viewport constraints
  getEffectiveWidth() {
    const { containerWidth, viewportWidth } = this.state;
    
    if (containerWidth === 0) {
      return 0;
    }

    // For web, the container might report maxWidth but be constrained by viewport
    // The actual rendered width should be the minimum of:
    // 1. Measured container width
    // 2. Viewport width minus margins/padding
    if (typeof window !== 'undefined' && viewportWidth > 0) {
      // Account for body margins and container padding in viewport calculation
      const viewportConstrainedWidth = Math.max(320, viewportWidth - 40); // Min 320px, subtract margins
      return Math.min(containerWidth, viewportConstrainedWidth);
    }

    return containerWidth;
  }

  // Determine optimal font size and line configuration
  calculateOptimalDisplay(text, containerWidth) {
    if (!text || containerWidth === 0) {
      return { fontSize: 35, lines: [text || ''] };
    }

    // Use effective width that considers viewport constraints
    const effectiveWidth = this.getEffectiveWidth();
    
    // Account for padding and give more conservative width calculation
    // Use 80px total padding to prevent edge cutoff (40px on each side)
    let availableWidth = effectiveWidth - 80;
    
    // For very small screens (mobile), be even more conservative
    if (effectiveWidth < 400) {
      availableWidth = effectiveWidth - 100; // Extra padding for mobile
    }
    
    // Try font size 45 first
    let fontSize = 45;
    let textWidth = this.calculateTextWidth(text, fontSize);
    
    if (textWidth <= availableWidth) {
      // Text fits in one line at 45px
      return { fontSize: 45, lines: [text] };
    }
    
    // Try font size 35
    fontSize = 35;
    textWidth = this.calculateTextWidth(text, fontSize);
    
    if (textWidth <= availableWidth) {
      // Text fits in one line at 35px
      return { fontSize: 35, lines: [text] };
    }
    
    // Need to split into multiple lines at 35px
    const lines = this.splitTextIntoLines(text, availableWidth, fontSize);
    return { fontSize: 35, lines };
  }

  onLayout = (event) => {
    const { width } = event.nativeEvent.layout;
    
    // For web, also consider the actual viewport constraints
    let effectiveWidth = width;
    if (typeof window !== 'undefined') {
      const viewportWidth = window.innerWidth;
      // If the measured width is close to maxWidth but viewport is smaller,
      // use the viewport-constrained width instead
      if (width > 600 && viewportWidth < width) {
        effectiveWidth = Math.max(320, viewportWidth - 40); // Account for margins
      }
    }
    
    this.setState({ 
      containerWidth: effectiveWidth,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0
    });
  };

  render() {
    const { text, style, gradientColors, fontFamily, fontWeight, ...otherProps } = this.props;
    const { containerWidth } = this.state;
    
    const { fontSize, lines } = this.calculateOptimalDisplay(text, containerWidth);
    
    // Calculate responsive width and center point for GradientText
    const effectiveWidth = this.getEffectiveWidth();
    const gradientWidth = Math.max(320, Math.min(600, effectiveWidth)); // Min 320px, max 600px
    const gradientCenterX = gradientWidth / 2;
    
    // Debug logging for mobile issues (remove in production)
    if (typeof window !== 'undefined' && window.innerWidth < 500) {
      console.log('ResponsiveGradientText Debug:', {
        text,
        containerWidth,
        viewportWidth: this.state.viewportWidth,
        effectiveWidth,
        gradientWidth,
        gradientCenterX,
        fontSize,
        lines
      });
    }
    
    return (
      <View 
        style={[style, { width: '100%' }]}
        onLayout={this.onLayout}
      >
        {lines.map((line, index) => (
          <GradientText
            key={index}
            text={line}
            fontSize={fontSize}
            isGradientFill
            style={[
              {
                textAlign: 'center',
                marginTop: index === 0 ? lines.length > 1 ? -10 : 0 : -50,
                marginBottom: index === lines.length - 1 ? -6 : -12,
                paddingHorizontal: 20,
                textTransform: 'uppercase',
                lineHeight: fontSize * 0.9, // Tighter line height for multi-line
              }
            ]}
            gradientColors={gradientColors || ["#464853", "#6b6e7d"]}
            fontFamily={fontFamily || "nunito"}
            fontWeight={fontWeight || 900}
            locations={{ x: gradientCenterX, y: 75 }}
            width={gradientWidth}
            {...otherProps}
          />
        ))}
      </View>
    );
  }
}
